import { useState } from 'react';
import Header from './Header';
import { SITE_DOMAIN, SUPPORT_EMAIL, SUPPORT_PHONE, SUPPORT_PHONE_TEL } from '../constants/site';
import { submitLead } from '../utils/submitLead';
import { openSalesIQChat } from '../utils/openSalesIQChat';

const heroItems = [
  'Step-by-step download & setup',
  'Upgrade paths & new-computer moves',
  'Backup, restore & TreeVault walkthroughs',
  'Sync and offline error recovery',
  'FamilySearch linking assistance',
  'Charts, media & report guidance',
];

export default function Hero() {
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    const form = event.currentTarget;
    const data = new FormData(form);
    const name = String(data.get('name') || '').trim();
    const email = String(data.get('email') || '').trim();
    const phone = String(data.get('phone') || '').trim();
    const message = String(data.get('message') || '').trim();

    if (!phone) {
      setError('Please enter your phone number.');
      return;
    }

    setPending(true);

    try {
      await submitLead({
        name,
        email,
        phone,
        message,
        source: 'hero-form',
        _honey: String(data.get('_honey') || ''),
      });
      setSent(true);
      form.reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setPending(false);
    }
  };

  return (
    <section className="w-full flex flex-col">
      <Header />

      <div className="t7-hero">
        <picture className="t7-hero__media">
          <source media="(max-width: 767px)" srcSet="/hero_bg-mobile.webp" type="image/webp" />
          <source srcSet="/hero_bg.webp" type="image/webp" />
          <img
            src="/hero_bg.jpg"
            alt=""
            width="1600"
            height="900"
            fetchPriority="high"
            decoding="async"
            className="t7-hero__img"
          />
        </picture>
        <div className="t7-hero__shade" aria-hidden="true" />
        <div className="t7-hero__glow" aria-hidden="true" />

        <div className="container t7-hero__inner">
          <div className="t7-hero__copy t7-rise">
            <p className="t7-kicker" style={{ color: '#c9956a' }}>
              {SITE_DOMAIN}
            </p>
            <h1 className="t7-hero__title">Your Family Tree Maker Support Hub</h1>
            <p className="text-[0.95rem] sm:text-base md:text-lg text-white/90 leading-relaxed mb-6 md:mb-8 max-w-xl">
              Whether you are installing for the first time or moving years of research to a new
              device, our team helps you get Family Tree Maker running smoothly again.
            </p>

            <ul className="t7-hero__list">
              {heroItems.map((item, i) => (
                <li key={item} className={`t7-rise t7-rise-delay-${Math.min(i + 1, 3)}`}>
                  <span className="t7-hero__dot" aria-hidden="true">✓</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <div className="t7-actions">
              <button type="button" className="t7-btn t7-btn--solid" onClick={openSalesIQChat}>
                Chat with Support
              </button>
            </div>
          </div>

          <aside className="t7-hero__form-wrap t7-rise t7-rise-delay-2" aria-label="Request help">
            {sent ? (
              <div className="t7-hero__form t7-hero__form--done">
                <p className="t7-kicker">Request received</p>
                <h2>We&apos;re on it</h2>
                <p>
                  Your message is in our queue. A specialist will reach out shortly. You can also
                  email us at <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
                </p>
                <button
                  type="button"
                  className="t7-btn t7-btn--green"
                  onClick={() => {
                    setSent(false);
                    setError('');
                  }}
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form className="t7-hero__form" onSubmit={handleSubmit}>
                <input
                  type="text"
                  name="_honey"
                  className="t7-hero__honeypot"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />

                <div className="t7-hero__form-head">
                  <p>Quick request</p>
                  <h2>Download & Support Form</h2>
                </div>

                <label htmlFor="hero-name">
                  Full name
                  <input
                    id="hero-name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    required
                    placeholder="Jane Smith"
                  />
                </label>

                <label htmlFor="hero-email">
                  Email
                  <input
                    id="hero-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    placeholder="you@email.com"
                  />
                </label>

                <label htmlFor="hero-phone">
                  Phone
                  <input
                    id="hero-phone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    required
                    placeholder="+1 555 000 0000"
                  />
                </label>

                <label htmlFor="hero-message">
                  What do you need help with?
                  <textarea
                    id="hero-message"
                    name="message"
                    required
                    rows={3}
                    placeholder="Install issue, upgrade, sync error, TreeVault…"
                  />
                </label>

                {error ? <p className="text-red-600 text-sm m-0">{error}</p> : null}

                <button type="submit" className="t7-btn t7-btn--green" disabled={pending}>
                  {pending ? 'Sending…' : 'Get help today'}
                </button>
              </form>
            )}

            <a className="t7-hero__call" href={`tel:${SUPPORT_PHONE_TEL}`}>
              Call Us : {SUPPORT_PHONE}
            </a>
          </aside>
        </div>
      </div>
    </section>
  );
}
